/**
 * PUBLICATION → GRID discovery hook.
 *
 * Mirrors the LOD0 Brand Image bounded discovery contract EXACTLY: the loaded
 * parcel SET → active persistent identities (ONE `$in` batch) → active PUBLIC
 * publication snapshots (ONE bounded batch) → renderable territories associated
 * to the CURRENT operational component via the canonical WO#1 territory
 * geometry authority (`associateRenderables`). No per-parcel / per-territory /
 * per-frame request and no unbounded scan; re-runs only when the loaded parcel
 * set actually changes (or discovery is toggled by entering the detail LOD).
 *
 * Zero-stale-authority: a monotonic generation + AbortController drop any older
 * response. Publication truth is the SAME backend authority used by the public
 * route — never the private Draft, never editor local state.
 */
import { useEffect, useRef, useState } from "react";

import { batchTerritoryIdentities } from "../lib/brand-image-api";
import { batchPublicPublications, type PublicSnapshot } from "../lib/publication-api";
import {
  associateRenderables,
  type RenderableTerritory,
  type UnitRect,
} from "./brand-image-geometry";

export interface PublicationRenderable {
  renderable: RenderableTerritory;
  snapshot: PublicSnapshot;
}

function parcelSetKey(parcels: readonly UnitRect[]): string {
  if (parcels.length === 0) return "";
  return parcels
    .map((p) => p.id)
    .sort((a, b) => a - b)
    .join(",");
}

export function usePublicationDiscovery(
  parcels: readonly UnitRect[],
  enabled = true,
  refreshKey = 0,
): PublicationRenderable[] {
  const [items, setItems] = useState<PublicationRenderable[]>([]);
  const genRef = useRef(0);
  const parcelsRef = useRef(parcels);
  parcelsRef.current = parcels;
  const key = parcelSetKey(parcels);

  useEffect(() => {
    const myGen = ++genRef.current;
    if (!enabled || key === "") {
      setItems([]);
      return;
    }
    const controller = new AbortController();
    const loaded = parcelsRef.current;

    (async () => {
      try {
        const ids = loaded.map((p) => p.id);
        const identities = await batchTerritoryIdentities(ids, controller.signal);
        if (myGen !== genRef.current) return;
        const identityIds = Array.from(new Set(identities.map((i) => i.identity_id)));
        if (identityIds.length === 0) {
          setItems([]);
          return;
        }
        const snaps = await batchPublicPublications(identityIds, controller.signal);
        if (myGen !== genRef.current) return;

        const byId = new Map(snaps.map((s) => [s.identity_id, s]));
        // Reuse the FROZEN brand-image association (expansion recentres; an
        // identity whose binding is not fully loaded is fail-soft skipped). Only
        // identities that actually have an active publication participate.
        const dims = new Map(snaps.map((s) => [s.identity_id, { width: 1, height: 1 }]));
        const discovered = identities.map((i) => ({
          identityId: i.identity_id,
          owner: i.owner,
          memberParcelIds: i.member_parcel_ids,
        }));
        const renderables = associateRenderables(parcelsRef.current, discovered, dims);
        if (myGen !== genRef.current) return;

        const next: PublicationRenderable[] = [];
        for (const r of renderables) {
          const snap = byId.get(r.identityId);
          if (snap) next.push({ renderable: r, snapshot: snap });
        }
        setItems(next);
      } catch {
        if (myGen !== genRef.current || controller.signal.aborted) return;
        setItems([]); // fail-soft: a discovery error shows no publications this gen
      }
    })();

    return () => controller.abort();
  }, [key, enabled, refreshKey]);

  return items;
}
